'use client'

import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { Animation, Button, EmptyState, Loader, Spacer, Text } from '@vinted/web-ui'

import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { logMessage } from '@marketplace-web/observability/logging-util'
import { InfiniteScroll } from '@marketplace-web/common-components/infinite-scroll-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import { SortByOption, CatalogAttribute, Filter } from '@marketplace-web/catalog/catalog-data'
import {
  ElectronicsVerificationBundleInfoBanner,
  ItemVerificationBundleInfoBanner,
} from '@marketplace-web/escrow-pricing/escrow-pricing-feature'

import {
  WardrobeCatalogWithParentIdModel,
  SellerFiltersType,
  clickEvent,
  clickFilterEvent,
  selectCatalogEvent,
} from '@marketplace-web/profile/profile-data'

import {
  BundleFooterWrapper,
  BundleHeader,
  BundlingLimitationsModal,
  useBundleSummary,
  resolveBundleError,
} from '@marketplace-web/bundles/items-bundle-feature'
import { CurrencyAmountModel } from '@marketplace-web/currency/currency-data'
import {
  BundlingLimitationsErrorType,
  exceedingLimitItemClickEvent,
} from '@marketplace-web/bundles/items-bundle-data'
import { ResponseError } from '@marketplace-web/core-api/api-client-util'

import { ITEM_UPLOAD_URL } from '../../../constants/routes'
import CatalogFilter from './CatalogFilter'
import ItemsContainer from './ItemsContainer'
import SellerFilters from './SellerFilters'
import SortFilter from './SortFilter'

type Props = {
  userId: number
  currentUserId: number | null | undefined
  allowBump: boolean
  userOnHoliday: boolean
  items: Array<ProductItemModel>
  itemsCount: number
  isLoading: boolean
  selectedSellerFilter: SellerFiltersType | undefined
  sortBy: SortByOption
  screen: string
  catalogId: number | null
  catalogs: Array<WardrobeCatalogWithParentIdModel>
  fetchMoreItems: (selectedFilter?: SellerFiltersType) => void
  updateFilters: (newSortBy: SortByOption, newCatalogId: number | null) => void
  totalItemsCount: number
  homepageSessionId: string | null
  onBundleItemSelect: (itemIds: Array<number> | null) => void
  selectedItems: Array<ProductItemModel>
  setSelectedItems: (items: Array<ProductItemModel>) => void
  showBundles: boolean
  bundleTrackingId: string | null
  isBundleDiscountEnabled: boolean
  onItemLimitationError: (id: string) => void
}

const trackingElementByFilterType: Record<
  SellerFiltersType,
  Parameters<typeof clickEvent>[0]['target']
> = {
  [SellerFiltersType.Active]: 'filter_active',
  [SellerFiltersType.Hidden]: 'filter_hidden',
  [SellerFiltersType.Sold]: 'filter_sold',
  [SellerFiltersType.Reserved]: 'filter_reserved',
  [SellerFiltersType.Drafts]: 'filter_drafts',
}

const ROOT_CATALOG_ID = -1

const UserItems = ({
  userId,
  currentUserId,
  allowBump,
  userOnHoliday,
  items,
  itemsCount,
  isLoading,
  selectedSellerFilter,
  sortBy,
  screen,
  catalogId,
  catalogs,
  fetchMoreItems,
  updateFilters,
  totalItemsCount,
  homepageSessionId,
  onBundleItemSelect,
  selectedItems,
  setSelectedItems,
  showBundles,
  bundleTrackingId,
  isBundleDiscountEnabled,
  onItemLimitationError,
}: Props) => {
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const asset = useAsset('/assets/animations')
  const translate = useTranslate()

  const [isClientSideReady, setIsClientSideReady] = useState(false)
  const [isBundleReviewLoading, setIsBundleReviewLoading] = useState(false)
  const [lastSelectedItemId, setLastSelectedItemId] = useState<string | null>(null)
  const [itemLimitationError, setItemLimitationError] = useState<{
    limit: CurrencyAmountModel | null
    type: BundlingLimitationsErrorType
  } | null>(null)

  const sellerId = userId
  const isViewingSelf = currentUserId === sellerId

  const bundlingSellerProfileAbTest = useAbTest('bundling_seller_profile_v2')
  const isBundlingSellerProfileAbTestOn =
    bundlingSellerProfileAbTest?.variant === 'a' || bundlingSellerProfileAbTest?.variant === 'b'
  const enabledStickyFilters = bundlingSellerProfileAbTest?.variant === 'b'

  const emptyWardrobeBodyUpdateExperiment = useAbTest('empty_wardrobe_seller_updated_copy')
  const isEmptyWardrobeBodyUpdateExperimentEnabled =
    emptyWardrobeBodyUpdateExperiment && emptyWardrobeBodyUpdateExperiment.variant !== 'off'

  const emptyWardrobeSellerUpdatedCopyTitleExperiment = useAbTest(
    'empty_wardrobe_seller_updated_copy_title',
  )
  const isEmptyWardrobeSellerUpdatedCopyTitleExperimentEnabled =
    emptyWardrobeSellerUpdatedCopyTitleExperiment &&
    emptyWardrobeSellerUpdatedCopyTitleExperiment.variant !== 'off'

  const emptyWardrobeBuyerUpdateExperiment = useAbTest('empty_wardrobe_buyer_updated_copy_v2')
  const isEmptyWardrobeBuyerUpdateExperimentEnabled =
    emptyWardrobeBuyerUpdateExperiment && emptyWardrobeBuyerUpdateExperiment.variant !== 'off'

  const emptyWardrobeBuyerUpdatedCopyBodyExperiment = useAbTest(
    'empty_wardrobe_buyer_updated_copy_body',
  )
  const isEmptyWardrobeBuyerUpdatedCopyBodyExperimentEnabled =
    emptyWardrobeBuyerUpdatedCopyBodyExperiment &&
    emptyWardrobeBuyerUpdatedCopyBodyExperiment.variant !== 'off'

  const holidayModeHiddenListingsCopyUpdateExperiment = useAbTest(
    'holiday_mode_hidden_listings_copy_update',
  )
  const isHolidayModeHiddenListingsCopyUpdateExperimentEnabled =
    holidayModeHiddenListingsCopyUpdateExperiment &&
    holidayModeHiddenListingsCopyUpdateExperiment.variant !== 'off'

  const trackAbTest = useTrackAbTestCallback()
  const { bundleSummary, isBundleSummaryLoading, bundleError, escrowNoteTranslationKey } =
    useBundleSummary({
      selectedItemIds: selectedItems.map(item => item.id),
      sellerId,
      isEnabled: showBundles && isBundlingSellerProfileAbTestOn,
      isItemAdded: !!lastSelectedItemId,
    })

  const clearItemLimitationError = () => {
    setItemLimitationError(null)
  }

  const handleItemLimitationError = useCallback(
    (type: BundlingLimitationsErrorType, limit: CurrencyAmountModel | null) => {
      setItemLimitationError({
        limit,
        type,
      })

      track(
        exceedingLimitItemClickEvent({
          screen,
          bundleSessionId: bundleTrackingId ?? '',
          itemId: lastSelectedItemId ?? '',
        }),
      )

      if (lastSelectedItemId) {
        setLastSelectedItemId(null)
        onItemLimitationError(lastSelectedItemId)
      }
    },
    [bundleTrackingId, lastSelectedItemId, onItemLimitationError, screen, track],
  )

  const handleItemError = useCallback(
    (error: ResponseError) => {
      const resolvedError = resolveBundleError(error)

      if (!resolvedError) return

      if (resolvedError.type === BundlingLimitationsErrorType.ItemExceedsLimit) {
        handleItemLimitationError(
          BundlingLimitationsErrorType.ItemExceedsLimit,
          resolvedError.limit,
        )
      }
    },
    [handleItemLimitationError],
  )

  useEffect(() => {
    if (bundleError && !itemLimitationError) {
      handleItemError(bundleError)
    }
  }, [bundleError, handleItemError, itemLimitationError])

  useEffect(() => {
    setIsClientSideReady(true)
  }, [])

  useEffect(() => {
    if (!showBundles) return

    trackAbTest(bundlingSellerProfileAbTest)
  }, [trackAbTest, bundlingSellerProfileAbTest, showBundles])

  useEffect(() => {
    if (!isViewingSelf) return

    trackAbTest(emptyWardrobeBodyUpdateExperiment)
  }, [trackAbTest, emptyWardrobeBodyUpdateExperiment, isViewingSelf])

  useEffect(() => {
    if (isViewingSelf || userOnHoliday || totalItemsCount !== 0 || isLoading || !isClientSideReady)
      return

    trackAbTest(emptyWardrobeBuyerUpdateExperiment)
  }, [
    trackAbTest,
    emptyWardrobeBuyerUpdateExperiment,
    isViewingSelf,
    userOnHoliday,
    totalItemsCount,
    isLoading,
    isClientSideReady,
    items.length,
  ])

  useEffect(() => {
    if (isViewingSelf || userOnHoliday || totalItemsCount !== 0 || isLoading || !isClientSideReady)
      return

    trackAbTest(emptyWardrobeBuyerUpdatedCopyBodyExperiment)
  }, [
    trackAbTest,
    emptyWardrobeBuyerUpdatedCopyBodyExperiment,
    isViewingSelf,
    userOnHoliday,
    totalItemsCount,
    isLoading,
    isClientSideReady,
    items.length,
  ])

  const getTrackOpenDropdown = (filterType: Filter) => () => {
    track(
      clickFilterEvent({
        filter: filterType,
        screen,
      }),
    )
  }

  useEffect(() => {
    if (!isViewingSelf || totalItemsCount !== 0 || userOnHoliday || isLoading || !isClientSideReady)
      return

    trackAbTest(emptyWardrobeSellerUpdatedCopyTitleExperiment)
  }, [
    trackAbTest,
    emptyWardrobeSellerUpdatedCopyTitleExperiment,
    isViewingSelf,
    totalItemsCount,
    userOnHoliday,
    isLoading,
    isClientSideReady,
    items.length,
  ])

  useEffect(() => {
    if (isViewingSelf || !userOnHoliday) return

    trackAbTest(holidayModeHiddenListingsCopyUpdateExperiment)
  }, [trackAbTest, holidayModeHiddenListingsCopyUpdateExperiment, isViewingSelf, userOnHoliday])

  const trackCatalogSelection = (id: number | null) => {
    track(
      selectCatalogEvent({
        catalogIds: id ? [id] : [ROOT_CATALOG_ID],
        attributeId: CatalogAttribute.ProfileFilters,
        screen,
      }),
    )
  }

  const trackSortBySelection = (sortByOption: SortByOption) => {
    track(
      clickEvent({
        target: 'filter_sorting_selection',
        screen,
        targetDetails: sortByOption,
      }),
    )
  }

  const handleItemClick = (item: ProductItemModel, isSelected: boolean) => {
    if (isSelected) {
      setLastSelectedItemId(item.id.toString())
    } else {
      setLastSelectedItemId(null)
    }
  }

  const handleCatalogIdChange = (newCatalogId: number | null) => {
    if (newCatalogId === catalogId) return

    updateFilters(sortBy, newCatalogId)
    trackCatalogSelection(newCatalogId)
  }

  const handleSortByChange = (newSortBy: SortByOption) => {
    if (newSortBy === sortBy) return

    updateFilters(newSortBy, catalogId)
    trackSortBySelection(newSortBy)
  }

  const handleSellerFilterClick = (filter: SellerFiltersType) => {
    const newFilter = selectedSellerFilter === filter ? undefined : filter

    const trackingEvent = clickEvent({
      target: trackingElementByFilterType[filter],
      screen: 'current_user_profile',
    })

    if (selectedSellerFilter !== filter) track(trackingEvent)

    logMessage(`Closet filter clicked: ${filter}`, {
      feature: 'closet_filtering',
      extra: filter || 'all',
    })

    fetchMoreItems(newFilter)
  }

  const handleReviewBundleLoading = (state: boolean) => {
    setIsBundleReviewLoading(state)
  }

  const fetchMore = () => {
    fetchMoreItems(selectedSellerFilter)
  }

  const updateSelectedItems = (updatedItems: Array<ProductItemModel>) => {
    setSelectedItems(updatedItems)
    onBundleItemSelect(updatedItems.map(({ id }) => id) ?? null)
  }

  const showEmptyState = isEmpty(items) && !isLoading && isClientSideReady
  const isBundleHeaderVisible =
    isBundlingSellerProfileAbTestOn &&
    selectedItems.length > 0 &&
    showBundles &&
    isBundleDiscountEnabled

  const renderEmptyState = () => {
    const animation = userOnHoliday ? 'vacation' : 'closet'

    if (isViewingSelf) {
      return (
        <EmptyState
          testId="closet-empty-state"
          animation={<Animation animationUrl={asset('closet-empty-state.json')} />}
          title={translate(
            `profile.items_empty_state.self_view.${isEmptyWardrobeSellerUpdatedCopyTitleExperimentEnabled ? 'experimental_title' : 'title'}`,
          )}
          body={translate(
            `profile.items_empty_state.self_view.${isEmptyWardrobeBodyUpdateExperimentEnabled ? 'experimental_subtitle_secondary' : 'subtitle_secondary'}`,
          )}
          action={
            <Button
              text={translate('profile.items_empty_state.button_secondary')}
              url={ITEM_UPLOAD_URL}
              styling="filled"
            />
          }
        />
      )
    }

    return (
      <EmptyState
        testId="closet-empty-state"
        title={translate(
          isEmptyWardrobeBuyerUpdateExperimentEnabled
            ? 'profile.items_empty_state.buyer_view.experimental_title'
            : 'user.items_empty.title',
        )}
        body={
          userOnHoliday
            ? translate(
                isHolidayModeHiddenListingsCopyUpdateExperimentEnabled
                  ? 'profile.on_holiday.subtitle'
                  : 'user.items_empty.on_holiday',
              )
            : translate(
                isEmptyWardrobeBuyerUpdatedCopyBodyExperimentEnabled
                  ? 'profile.items_empty_state.buyer_view.subtitle'
                  : 'user.items_empty.no_items',
              )
        }
        animation={<Animation animationUrl={asset(`${animation}-empty-state.json`)} />}
      />
    )
  }

  const renderItemsContainer = () => (
    <ItemsContainer
      items={items}
      userId={sellerId}
      allowBump={allowBump}
      isViewingSelf={isViewingSelf}
      updateSelectedItems={updateSelectedItems}
      selectedItems={selectedItems}
      bundleTrackingId={bundleTrackingId}
      showBundles={showBundles}
      disabled={isBundleReviewLoading}
      onItemClick={(item, _, isSelected) => handleItemClick(item, isSelected)}
    />
  )

  const renderLoader = () => (
    <div className="u-flexbox u-justify-content-center">
      <Loader size="large" testId="closet-loader" />
    </div>
  )

  const renderFilters = () => {
    return (
      <div className="u-text-left u-flexbox u-flex-wrap" data-testid="closet-buyer-filters">
        <CatalogFilter
          catalogs={catalogs}
          selectedId={catalogId}
          toggleCatalogFilterOption={handleCatalogIdChange}
          onDropdownOpen={getTrackOpenDropdown(Filter.Category)}
        />
        <SortFilter
          selected={sortBy}
          onItemClick={handleSortByChange}
          onDropdownOpen={getTrackOpenDropdown(Filter.Sort)}
        />
      </div>
    )
  }

  const renderBuyerFilters = () => {
    if (!isViewingSelf && catalogs.length) return renderFilters()

    return null
  }

  const renderItems = () => {
    const hasItems = totalItemsCount > 0

    const bundleItemsCountAndFilters = classNames('bundle-items-count-and-filters', {
      'u-justify-content-between': hasItems,
      'u-justify-content-flex-end': !hasItems,
      'u-flex-direction-column': breakpoints.phones,
      'bundle-items-count-and-filters--sticky': enabledStickyFilters && !isViewingSelf,
      'bundle-items-count-and-filters--sticky__with-header':
        isBundleHeaderVisible && enabledStickyFilters && !isViewingSelf,
    })

    return (
      <>
        <div className={bundleItemsCountAndFilters}>
          {hasItems && (
            <div className="u-flexbox u-justify-content-between u-align-items-center u-flex-grow u-flex-wrap">
              <Text
                as="h2"
                type={isBundlingSellerProfileAbTestOn ? 'subtitle' : 'title'}
                theme={isBundlingSellerProfileAbTestOn ? 'muted' : undefined}
                text={translate('user.items.title', { count: itemsCount })}
              />
              {renderBuyerFilters()}
            </div>
          )}
          {isViewingSelf && (
            <SellerFilters
              onFilterClick={handleSellerFilterClick}
              selectedSellerFilter={selectedSellerFilter}
            />
          )}
        </div>
        {items.length > 0 && (
          <div className="u-ui-margin-horizontal-regular@portables">
            <InfiniteScroll
              renderItems={renderItemsContainer}
              onPageEnd={fetchMore}
              cursor={`user-items-${itemsCount}`}
            />
          </div>
        )}
      </>
    )
  }

  const renderVerificationSpacer = () => {
    if (isBundleHeaderVisible) return null

    return <Spacer size={breakpoints.desktops ? 'large' : 'medium'} />
  }

  const renderBundleVerificationBanner = () => {
    if (!isBundlingSellerProfileAbTestOn || !selectedItems.length || !bundleSummary || !showBundles)
      return null

    return (
      <>
        {bundleSummary.containsOfflineVerificationItem && (
          <>
            {renderVerificationSpacer()}
            <ItemVerificationBundleInfoBanner
              itemVerificationFee={bundleSummary.offlineVerificationFee}
            />
          </>
        )}
        {bundleSummary.containsElectronicsVerificationItem && (
          <>
            {renderVerificationSpacer()}
            <ElectronicsVerificationBundleInfoBanner
              electronicsVerificationFee={bundleSummary.electronicsVerificationFee}
            />
          </>
        )}
      </>
    )
  }

  return (
    <div className="u-z-index-isolate">
      {isBundleHeaderVisible && <BundleHeader discountHint={bundleSummary?.discountText} />}
      {renderBundleVerificationBanner()}
      {renderItems()}
      {showEmptyState && renderEmptyState()}
      {isLoading && renderLoader()}
      {isBundlingSellerProfileAbTestOn && selectedItems.length > 0 && showBundles && (
        <BundleFooterWrapper
          selectedItems={selectedItems}
          bundleSummary={bundleSummary}
          bundleTrackingId={bundleTrackingId}
          isSellerBusiness={items[0]?.user.isBusiness || false}
          homepageSessionId={homepageSessionId}
          sellerId={sellerId}
          isBundleSummaryLoading={isBundleSummaryLoading}
          transactionId={null}
          show={selectedItems.length > 0 && totalItemsCount > 1}
          warnOnUnsavedChanges
          screen={screen}
          onReviewBundleLoading={state => handleReviewBundleLoading(state)}
          escrowNoteTranslationKey={escrowNoteTranslationKey}
        />
      )}
      {!!itemLimitationError && (
        <BundlingLimitationsModal
          isOpen={!!itemLimitationError}
          limit={itemLimitationError.limit}
          type={itemLimitationError.type}
          bundleTrackingId={bundleTrackingId}
          screen={screen}
          onClose={clearItemLimitationError}
        />
      )}
    </div>
  )
}

export default UserItems
